import React from 'react';
import { Box, Typography, Button, Divider, TextField, Chip, Tooltip } from '@mui/material';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AppDocuServiceCall from '../_Utils/AppDocuServiceCall';
import getTexts from './i18n';
import { formatAiResponse, MarkdownRenderer } from './helpers';
import { documentTypeOptions } from '../_Utils/AppDocuCommon';

// Constants
const ROLES = {
  USER: 'user',
  ASSISTANT: 'assistant'
};

const NEXT_ACTIONS = {
  PROVIDE_DATA: 'provide_data',
  ASK_MANUAL_QUESTION: 'ask_manual_question'
};

const SELECTION_TYPES = ['brand', 'model', 'engine_code', 'petrol_ecu', 'intent'];

/**
 * Props:
 * - lang, comBrand
 * - t (translations object)
 * - onOpenDownloadDialog(type, id)
 */
export default class ChatComponent extends React.Component {
  constructor(props){
    super(props);
    this.state = {
      chatMessages: [], // {role:'user'|'assistant', content:string, payload?:apiResponse}
      chatInput: '',
      chatState: null, // mirrors server state object {brand,model,engine_code,petrol_ecu,intent,language,question}
      chatLoading: false,
      recentQuestions: [],
      finalReached: false, // true when next_action.value indicates no further interaction required
      forceDisableInput: false, // when user must choose among chips (selection_type scenario)
      followUp: "", // server session ID for chat continuity
    };
    this.chatInputRef = React.createRef();
    this.messagesContainerRef = React.createRef();
  }

  async componentDidMount(){
    await this.reloadRecentQuestions();
    // ensure we start scrolled to bottom if there are any messages
    this.scrollToBottom();
  }

  componentDidUpdate(prevProps, prevState){
    // Auto-scroll when messages change, loading state changes, or finalReached toggles
    if(prevState.chatMessages !== this.state.chatMessages || prevState.chatLoading !== this.state.chatLoading || prevState.finalReached !== this.state.finalReached){
      // Defer slightly to allow DOM to update
      setTimeout(()=> this.scrollToBottom('smooth'), 50);
    }
  }

  scrollToBottom = (behavior = 'auto') => {
    const el = this.messagesContainerRef && this.messagesContainerRef.current;
    if(!el) return;
    try{
      el.scrollTo({ top: el.scrollHeight, behavior });
    }catch(e){
      el.scrollTop = el.scrollHeight;
    }
  }

  reloadRecentQuestions = async () => {
    try {
      const resp = await AppDocuServiceCall.feasibilityListQuestion();
      if(resp?.data?.is_valid){
        this.setState({ recentQuestions: resp.data.result });
      }
    } catch(err){
      console.warn('Recent questions load failed', err);
    }
  }

  handleChatInputChange = (e) => {
    this.setState({ chatInput: e.target.value });
  };

  addErrorMessage = (content) => {
    const errMsg = { role: ROLES.ASSISTANT, content };
    this.setState(prev => ({
      chatMessages: [...prev.chatMessages, errMsg],
      chatLoading: false,
      forceDisableInput: false
    }));
  }

  handleChatSend = async () => {
    const { chatInput, chatState, chatMessages } = this.state;
    const text = chatInput.trim();
    if(!text) return;
    const selectionType = this.computeSelectionTypeForFreeText();
    const userMsg = { role:'user', content:text };
    this.setState({ chatMessages:[...chatMessages, userMsg], chatInput:'', chatLoading:true, finalReached:false });
    try {
      const res = await AppDocuServiceCall.feasibilityChatAi(text, this.state.followUp, chatState, selectionType, this.props.comBrand);
      this.applyServerResponse(res.data);
    } catch(err){
      this.addErrorMessage('Errore di comunicazione con il servizio AI.');
    }
  };

  computeSelectionTypeForFreeText = () => {
    const lastAssistant = this.getLastAssistantMessage();
    const isManualQuestion = lastAssistant?.payload?.next_action.value === NEXT_ACTIONS.ASK_MANUAL_QUESTION;
    const isFollowUpQuestion = this.state.chatState?.intent === 'manual_info' && this.state.chatState?.doc_id;    
    return (isManualQuestion || isFollowUpQuestion) ? 'question' : undefined;
  }

  applyServerResponse = (data) => {
    const payload = data || {};
    
    if (!payload.is_valid) {
      this.addErrorMessage(payload.message);
      return;
    }
    const assistantContent = this.renderAssistantPlainText(payload);
    const assistantMsg = { role:'assistant', content: assistantContent, payload };
    const isFinal = this.isFinalResponse(payload);
    const mustSelect = this.mustSelectOption(payload);
    
    // Preserve doc_id in state for follow-up questions
    let updatedState = payload.state;
    if(payload.manual_answer?.doc_id && payload.state?.intent === 'manual_info'){
      updatedState = { ...payload.state, doc_id: payload.manual_answer.doc_id };
    }
    
    this.setState(prev=>({
      chatMessages:[...prev.chatMessages, assistantMsg],
      chatState: updatedState,
      chatLoading:false,
      finalReached: isFinal,
      forceDisableInput: mustSelect,
      followUp: payload.follow_up,
    }));
  }

  getLastAssistantMessage = () => {
    return [...this.state.chatMessages].reverse().find(m => m.role === ROLES.ASSISTANT);
  }

  mustSelectOption = (payload) => {
    if (!payload?.next_action?.value) return false;
    return SELECTION_TYPES.includes(payload.next_action.value);
  }

  isFinalResponse = (payload) => {
    if (!payload) return false;
    
    const nextAction = payload.next_action?.value;
    
    // No next action means conversation is complete
    if (!nextAction) return true;
    
    // Special case: manual_info with doc_id keeps chat active for follow-up questions
    if (nextAction === NEXT_ACTIONS.PROVIDE_DATA) {
      const isManualInfoWithDoc = payload.state?.intent === 'manual_info' && payload.manual_answer?.doc_id;
      return !isManualInfoWithDoc;
    }
    
    return false;
  }

  handleChipQuickSend = async (value) => {
    const lastAssistant = this.getLastAssistantMessage();
    const selectionType = lastAssistant?.payload?.next_action.value;
    
    if (!selectionType) {
      this.setState({ chatInput: value }, () => this.handleChatSend());
      return;
    }
    const { chatState, chatMessages } = this.state;
    const userMsg = { role:'user', content:value };
    this.setState({ chatMessages:[...chatMessages, userMsg], chatLoading:true, forceDisableInput:false });
    try {
      const res = await AppDocuServiceCall.feasibilityChatAi(value, this.state.followUp, chatState, selectionType, this.props.comBrand);
      this.applyServerResponse(res.data);
    } catch(err){
      this.addErrorMessage('Errore di comunicazione con il servizio AI.');
    }
  };

  renderAssistantPlainText = (payload) => {
    const T = this.props.t;
    // Build a minimal textual summary for screen readers / plain view
    const { state, next_action, vehicle_data, manual_answer } = payload;
    const lines = [];

    // Vehicle display: prefer state values, fall back to vehicle_data.vehicle (backend can return either)
    const fromState = state && [state.brand, state.model, state.engine_code, state.petrol_ecu]
      .filter(v => v && v !== '__empty__')
      .join(' ');
    const fromVehicleData = vehicle_data?.vehicle && [vehicle_data.vehicle.brand, vehicle_data.vehicle.model, vehicle_data.vehicle.engine_code, vehicle_data.vehicle.petrol_ecu]
      .filter(v => v && v !== '__empty__')
      .join(' ');
    const veh = fromState || fromVehicleData;
    if (veh) lines.push(`${T.chatAiVehicleLabel}: **${veh.toUpperCase()}**`);

    if (state?.intent) {
      // Try i18n key first (e.g. T.chatIntent_download_calibration), then raw key
      const i18nKey = `chatIntent_${state.intent}`;
      const intentLabel = T[i18nKey] ? T[i18nKey] : state.intent;
      lines.push(`${T.chatAiIntentLabel}: ${intentLabel}`);
    }

    if (state?.question && state.intent==='manual_info') lines.push(`${T.chatAiQuestionLabel}: ${state.question}`);

    if (next_action.value === NEXT_ACTIONS.ASK_MANUAL_QUESTION) {
      lines.push('Inserisci la domanda sul manuale.');
    }

    if (next_action.value === NEXT_ACTIONS.PROVIDE_DATA) {
      if (!vehicle_data && !manual_answer) {
        lines.push('**Nessuna informazione disponibile.**');
      }
    }
    return lines.join('\n') || '...';
  };

  handleChatDocClick = (docHashOrId, meta) => {
    if(!meta) return;
    const doc = (meta.machines||[]).flatMap(m=>m.documents||[]).find(d=> d.file_hash===docHashOrId || d.id===docHashOrId);
    if(doc){
      this.props.onOpenDownloadDialog('document', doc.id);
    }
  };

  getOptionsChipColor = (next_action_value) => {
    switch (next_action_value) {
      case 'brand': return 'primary';
      case 'model': return 'default';
      case 'engine_code': return 'secondary';
      case 'petrol_ecu': return 'warning';
      case 'intent': return 'success';
    }
    return 'primary';
  }

  render(){
    const T = this.props.t;
    // Find the last assistant message that offers selectable options (not provide_data / ask_manual_question)
    const lastSelectableIndex = (() => {
      const msgs = this.state.chatMessages || [];
      return msgs.length - 1;
    })();
    
    // Calculate dynamic height based on navbarHeight prop
    const navbarHeightPx = parseInt(this.props.navbarHeight, 10) || 84;
    const chatHeight = `calc(100vh - ${navbarHeightPx + 170}px)`; // 170px for title, controls and margins
    
    return (
      <Box sx={{ display:'flex', flexDirection:'column', height: chatHeight, border:'1px solid #ddd', borderRadius:1, p:2, pt:1 }}>
        <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:1 }}>
          <Typography variant="subtitle2" color="text.secondary">Chat AI</Typography>
          <Button
            size="small"
            variant='text'
            color='primary'
            onClick={()=> this.setState({ 
              chatMessages:[], 
              chatState:null, 
              chatInput:'', 
              finalReached:false, 
              forceDisableInput:false,
              followUp: "", 
            }, this.reloadRecentQuestions)}>{T.chatAiNewChat}</Button>
        </Box>

        <Box ref={this.messagesContainerRef} sx={{ flex:1, overflowY:'auto', mb:1, pr:1 }}>

          {/* Ultime domande */}
          {this.state.chatMessages.length===0 && !!this.state.recentQuestions.length && (
            <Box sx={{ mb:2 }}>
              <Typography variant="caption" color="text.secondary">{T.chatAiRecentQuestions}</Typography>
              <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', mt:1 }}>
                {this.state.recentQuestions.map(q =>
                  <Button 
                    key={q.id} 
                    size="small" 
                    variant="outlined" 
                    onClick={()=> this.setState({ chatInput:q.message })}>{q.message}</Button>
                )}
              </Box>
              <Divider sx={{ my:2 }} />
            </Box>
          )}

          {/* Chat messages */}
          {this.state.chatMessages.map((m,i)=>(
            <Box key={i} sx={{ mb:2, display:'flex', flexDirection:'column', alignItems: m.role==='user' ? 'flex-end':'flex-start' }}>
              <Box sx={{ maxWidth:'85%', bgcolor: m.role==='user'? '#1976d2':'#f5f5f5', color: m.role==='user'? 'white':'black', p:1.5, borderRadius:2, whiteSpace:'pre-wrap', fontSize:14 }}>
                <MarkdownRenderer onDocClick={(hash)=> this.handleChatDocClick(hash, m.meta)} userRole={m.role}>{m.content}</MarkdownRenderer>

                {/* Selection CHIPS based on next_action.options */}
                {m.role === ROLES.ASSISTANT && !!m.payload.next_action.options?.length && (
                  <Box sx={{ mt:1 }}>
                    {m.payload.next_action.value !== '' && (
                      <Typography variant="caption" sx={{ display:'block', mb:0.5 }}>
                        {T.chatAiSelect + " " + (T.chatAiNextActions[m.payload.next_action.value] || m.payload.next_action.value)}
                      </Typography>                    
                    )}
                    <Box sx={{ display:'flex', flexWrap:'wrap', gap:1 }}>
                      {m.payload.next_action.options.slice(0,30).map(opt => {
                        const chipColor = this.getOptionsChipColor(m.payload.next_action.value);
                        const chipLabel = T.chatAiIntents?.[opt] || T[`chatIntent_${opt}`] || opt;
                        return <Chip 
                          key={opt} 
                          label={String(chipLabel).toUpperCase()} 
                          size="small" 
                          color={chipColor}
                          onClick={()=> this.handleChipQuickSend(opt)} 
                          disabled={i !== lastSelectableIndex} 
                          clickable={i === lastSelectableIndex} />
                      })}                      
                    </Box>
                  </Box>
                )}

                {/* Data presentation when provide_data */}
                {m.role === ROLES.ASSISTANT && m.payload?.next_action.value === NEXT_ACTIONS.PROVIDE_DATA && m.payload?.vehicle_data && (
                  <Box sx={{ mt:1, display:'flex', flexDirection:'column', gap:1 }}>

                    {/* Codes */}
                    {m.payload.state?.intent==='order_codes' && (
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight:600 }}>Pacchetti:</Typography>
                        {m.payload.vehicle_data.codes?.length>0 ? (
                          <ul style={{ paddingLeft: 0, listStyle: 'none' }}>
                          {m.payload.vehicle_data.codes.map(item => (
                            <li key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                              <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fafafa', p: 0.5, borderRadius: 1, border: '1px solid #eee', cursor: item.id ? 'pointer' : 'default' }} onClick={() => item.id && this.props.onOpenDownloadDialog('package', item.id)}>
                                <Tooltip title={item.name}>
                                  <Inventory2OutlinedIcon sx={{ color: '#ff9800', fontSize: 18, mr: 0.5 }} titleAccess="Package" />
                                </Tooltip>
                                <span style={{ fontWeight: 600, display: 'inline-block', lineHeight: 1.1 }}>
                                  <span style={{ fontWeight: 700, fontSize: 14 }}>{item.name}</span>
                                  {item.ecu_type && (<span style={{ display: 'block', fontSize: 12, color: '#1976d2', fontWeight: 600, lineHeight: 1, marginTop: 0 }}>{item.ecu_type}</span>)}
                                </span>
                              </Box>
                            </li>
                          ))}
                          </ul>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ mt:0.5 }}>Nessun pacchetto disponibile.</Typography>
                        )}
                      </Box>
                    )}

                    {/* Calibrations */}
                    {m.payload.state?.intent==='download_calibration' && (
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight:600 }}>Calibrazioni:</Typography>
                        {m.payload.vehicle_data.calibrations?.length>0 ? (
                          <ul style={{ paddingLeft: 0, listStyle: 'none' }}>
                          {m.payload.vehicle_data.calibrations.map(item => (
                            <li key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, bgcolor: '#fafafa', p: 0.5, borderRadius: 1, border: '1px solid #eee' }}>
                                <Tooltip title={item.name}>
                                  <ListAltOutlinedIcon sx={{ color: '#388e3c', fontSize: 18, mr: 0.5 }} titleAccess="Calibration" />
                                </Tooltip>
                                <span style={{ fontWeight: 600, cursor: item.id ? 'pointer' : 'default', display: 'inline-block', lineHeight: 1.1 }} onClick={() => item.id && this.props.onOpenDownloadDialog('calibration', item.id)}>
                                  <span style={{ fontWeight: 700, fontSize: 14 }}>{item.name || item.file_hash}</span>
                                  {item.ecu_type && (<span style={{ display: 'block', fontSize: 12, color: '#1976d2', fontWeight: 600, lineHeight: 1, marginTop: 0 }}>{item.ecu_type}</span>)}
                                </span>
                              </Box>
                            </li>
                          ))}
                          </ul>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ mt:0.5 }}>Nessuna calibrazione disponibile.</Typography>
                        )}
                      </Box>
                    )}

                    {/* Documents */}
                    {m.payload.state?.intent==='download_manual' && (                    
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight:600 }}>Documenti:</Typography>
                        {m.payload.vehicle_data.documents?.length>0 ? (                        
                          <ul style={{ paddingLeft: 0, listStyle: 'none' }}>
                          {m.payload.vehicle_data.documents.map(item=> {
                            const docType = documentTypeOptions.find(opt => opt.name === item.document_type);
                            return (
                              <li key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, bgcolor: '#fafafa', p: 0.5, borderRadius: 1, border: '1px solid #eee' }}>
                                  <Tooltip title={docType?.label || item.document_type}>
                                    <InsertDriveFileIcon fontSize="small" sx={{ color: docType?.color || 'info.main' }} />
                                  </Tooltip>
                                  <span style={{ fontWeight: 600, cursor: 'pointer', display: 'inline-block', lineHeight: 1.1 }} onClick={() => this.props.onOpenDownloadDialog('document', item.id)}>
                                    <span style={{ fontWeight: 700, fontSize: 14 }}>{item.name}</span>
                                    {item.ecu_type && (<span style={{ display: 'block', fontSize: 12, color: '#888', fontWeight: 500, lineHeight: 1, marginTop: 0 }}>{item.com_brand ? item.com_brand + ' - ' : ''}{item.ecu_type}</span>)}
                                  </span>
                                </Box>
                              </li>
                            )}
                          )}
                          </ul>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ mt:0.5 }}>Nessun documento disponibile.</Typography>
                        )}
                      </Box>
                    )}

                    {/* Manual answer (manual_info intent) */}
                    {m.payload.state?.intent === 'manual_info' && m.payload?.manual_answer && (
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight:600 }}>Risposta:</Typography>
                        {m.payload.manual_answer.error === null ? (
                          <>
                            <Box sx={{ mt:0.5, p:1, bgcolor:'#fafafa', border:'1px solid #eee', borderRadius:1 }}>
                              <MarkdownRenderer onDocClick={(hash)=> this.handleChatDocClick(hash, m.meta)} userRole={m.role}>{formatAiResponse(m.payload.manual_answer.text, m.payload.manual_answer.doc_hash)}</MarkdownRenderer>
                            </Box>
                            <Box sx={{ mt:1, display:'flex', gap:1, flexWrap:'wrap' }}>
                              <Button size="small" variant="outlined" onClick={()=> this.props.onOpenDownloadDialog('document', m.payload.manual_answer.doc_id)}>{T.chatAiManualDocDetails}</Button>
                            </Box>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ mt:0.5 }}>{m.payload.manual_answer.error}</Typography>
                        )}
                      </Box>
                    )}

                  </Box>
                )}
              </Box>
            </Box>
          ))}

          {this.state.chatLoading && <Typography variant="caption">...</Typography>}
        </Box>

        <Divider sx={{ mb:1 }} />

        <Box component="form" onSubmit={(e)=>{ e.preventDefault(); this.handleChatSend(); }} sx={{ display:'flex', gap:1 }}>
          {this.state.finalReached ? (
            <Button
              fullWidth
              variant="contained"
              onClick={()=> this.setState({ chatMessages:[], chatState:null, chatInput:'', finalReached:false, forceDisableInput:false, followUp: "" }, this.reloadRecentQuestions)}>
              {T.chatAiNewChat}
            </Button>
          ) : (
            <>
              <TextField
                fullWidth
                placeholder={this.computeInputPlaceholder(T)}
                value={this.state.chatInput}
                onChange={this.handleChatInputChange}
                size="small"
                multiline
                maxRows={6}
                inputRef={this.chatInputRef}
                onKeyDown={(e)=>{ if(e.key==='Enter' && !e.shiftKey){ e.preventDefault(); this.handleChatSend(); } }}
                disabled={this.state.forceDisableInput || this.state.chatLoading}
              />
              <Button 
                variant="contained" 
                onClick={this.handleChatSend} 
                disabled={this.state.chatLoading || !this.state.chatInput.trim() || this.state.forceDisableInput}>
                {T.chatAiSend}
              </Button>
            </>
          )}
        </Box>

      </Box>
    );
  }
}

ChatComponent.prototype.computeInputPlaceholder = function(T) {
  if (this.state.forceDisableInput) {
    const lastAssistant = this.getLastAssistantMessage();
    const sel = lastAssistant?.payload?.next_action.value;
    return sel ? `Seleziona ${sel} cliccando su una delle opzioni` : "Seleziona un'opzione";
  }
  
  const lastAssistant = this.getLastAssistantMessage();
  if (lastAssistant?.payload?.next_action.value === NEXT_ACTIONS.ASK_MANUAL_QUESTION) {
    return 'Inserisci la domanda sul manuale...';
  }
  
  // Check if we're in manual_info mode with doc_id for follow-up questions
  if (this.state.chatState?.intent === 'manual_info' && this.state.chatState?.doc_id) {
    return 'Fai una nuova domanda su questo documento...';
  }
  
  return T.chatAiPlaceholder;
}
